import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import {
  FaCloudDownloadAlt,
  FaGoogleDrive,
  FaFilm,
  FaClock,
  FaDollarSign,
  FaMagic,
  FaFileUpload,
  FaTimes
} from "react-icons/fa";
import { FiSend } from "react-icons/fi";
import { GrMicrophone } from "react-icons/gr";
import { PiFolderSimpleLight } from "react-icons/pi";
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import SubNav from "../components/subNav";
import SEO from '../components/SEO';
import styles from '../styles/script.module.css';
import ChatBot from './chatbot';
import { Camera, Film } from 'lucide-react';
import '../styles/initial.css';
import { baseUrl } from '../config';

// Existing Animation Styles
const animationStyles = `
  @keyframes filmStripFloat {
    0% {
      transform: translateY(-10px) rotate(0deg);
      opacity: 0.1;
    }
    50% {
      transform: translateY(10px) rotate(180deg);
      opacity: 0.15;
    }
    100% {
      transform: translateY(-10px) rotate(360deg);
      opacity: 0.1;
    }
  }

  @keyframes spotlightSweep {
    0% {
      transform: translate(-50%, -50%) rotate(0deg);
      opacity: 0.05;
    }
    100% {
      transform: translate(-50%, -50%) rotate(360deg);
      opacity: 0.1;
    }
  }

  .pb-film-background {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: -1;
    overflow: hidden;
    background: linear-gradient(to bottom right, rgb(255, 255, 255), rgb(227, 237, 234));
  }

  .pb-film-strip {
    position: absolute;
    width: 80px;
    height: 300px;
    background: repeating-linear-gradient(
      transparent 0px,
      transparent 15px,
      rgba(217, 119, 6, 0.55) 15px,
      rgb(213, 217, 6) 30px
    );
    animation: filmStripFloat 30s infinite ease-in-out;
  }

  .pb-spotlight {
    position: absolute;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(217, 119, 6, 0.64) 0%, transparent 70%);
    animation: spotlightSweep 25s infinite linear;
  }

  .pb-camera-icon {
    position: absolute;
    width: 40px;
    height: 40px;
    opacity: 0.1;
    background: rgb(14, 255, 191);
    clip-path: polygon(15% 25%, 85% 25%, 85% 75%, 15% 75%);
  }

  /* Loading Spinner Styles */
  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    background: linear-gradient(to bottom right, rgb(255, 255, 255), rgb(227, 237, 234));
  }

  .loading-spinner {
    width: 80px;
    height: 80px;
    border: 8px solid #e0f2fe;
    border-top-color: #14b8a6;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading-text {
    margin-top: 24px;
    font-size: 24px;
    font-weight: 600;
    color: #14b8a6;
    text-align: center;
  }

  .loading-subtext {
    margin-top: 8px;
    font-size: 16px;
    color: #64748b;
    text-align: center;
  }
`;

const ScriptAi = () => {
  const [inputValue, setInputValue] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState('');
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('success');
  const [isRecording, setIsRecording] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [recognitionError, setRecognitionError] = useState('');
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const [activePage, setActivePage] = useState(1);
  const [activeUser, setActiveUser] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [selectedConversationIndex, setSelectedConversationIndex] = useState(0);

  // New states for passing to ChatBot
  const [initialPrompt, setInitialPrompt] = useState('');
  const [fileToSend, setFileToSend] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // ✅ NEW: State for checking existing conversations
  const [isCheckingExisting, setIsCheckingExisting] = useState(true);

  // ✅ NEW: Real project genre/type, fetched from the project record
  const [projectInfo, setProjectInfo] = useState({ genre: '', project_type: '' });

  // Benefits data for the enhanced UI
  const benefitsData = [
    {
      icon: <FaClock className="text-4xl text-teal-500" />,
      title: "Time Efficiency",
      description: "Reduce script development time by up to 70%",
      details: "Our AI-powered tool streamlines the scriptwriting process, helping you generate high-quality scripts in a fraction of the traditional time."
    },
    {
      icon: <FaDollarSign className="text-4xl text-teal-500" />,
      title: "Cost Reduction",
      description: "Minimize production expenses",
      details: "By automating script analysis and generation, you'll significantly cut down on writing team costs and iterative development expenses."
    },
    {
      icon: <FaMagic className="text-4xl text-teal-500" />,
      title: "Creative Enhancement",
      description: "Unlock new storytelling possibilities",
      details: "Our advanced AI provides creative suggestions, helping you break through writer's block and explore innovative narrative structures."
    }
  ];

  // ✅ Handle returning from script editor
  useEffect(() => {
    const state = location.state;
    if (state && state.activePage === 2 && state.shouldLoadConversation) {
      setIsCheckingExisting(false); // Skip checking
      setActivePage(2);

      if (state.conversationId) {
        loadConversationById(state.conversationId);
      }
    }
  }, [location.state]);

  // ✅ Check for existing conversations on mount
  useEffect(() => {
    checkProjectPermissions("Script Analysis");
    fetchProjectInfo();
  }, []);

  // ✅ NEW: Check for existing conversations after permissions verified
  useEffect(() => {
    const checkExistingConversations = async () => {
      // Don't check if returning from script editor
      if (location.state?.activePage === 2) {
        return;
      }

      if (!projectId || !activeUser) return;

      setIsCheckingExisting(true);

      try {
        const response = await fetch(`${baseUrl}/scriptConversation/getConversationHistory`, {
          headers: {
            'project_id': projectId,
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          const existingConversations = data.conversations;

          // ✅ If conversations exist, load the most recent one and go to chatbot
          if (existingConversations && existingConversations.length > 0) {
            const latestConversation = existingConversations[0]; // Most recent
            await loadExistingConversation(latestConversation.id);
            return; // Don't show script.jsx page
          } else {
          }
        }
      } catch (error) {
      } finally {
        setIsCheckingExisting(false);
      }
    };

    // Only check after user is verified
    if (activeUser && projectId) {
      checkExistingConversations();
    }
  }, [activeUser, projectId]);

  // ✅ NEW: Load existing conversation and navigate to chatbot
  const loadExistingConversation = async (conversationId) => {
    try {
      const response = await fetch(`${baseUrl}/scriptConversation/getConversation`, {
        headers: {
          'conversation_id': conversationId,
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load conversation');
      }

      const data = await response.json();

      // ✅ Convert backend format to frontend format
      const loadedConversation = {
        name: data.title || 'Script Generation',
        conversationId: data.id,
        messages: data.messages.map(msg => ({
          text: msg.content,
          fromMe: msg.message_type === 'user',
          timestamp: msg.created_at,
          status: 'read',
          id: msg.id,
          sceneBreakdown: msg.message_type === 'ai' ? {
            genre: msg.genre,
            characters: msg.characters,
            locations: msg.locations,
            num_scenes: msg.num_scenes,
            shooting_days: msg.shooting_days,
            vfx_required: msg.vfx_required,
            complexity_score: msg.complexity_score
          } : null
        }))
      };

      // ✅ Set conversations and navigate to chatbot
      setConversations([loadedConversation]);
      setActivePage(2); // Go directly to chatbot
      setIsCheckingExisting(false);
    } catch (error) {
      setIsCheckingExisting(false);
    }
  };

  const loadConversationById = async (convId) => {
    try {
      const response = await fetch(`${baseUrl}/scriptConversation/getConversation`, {
        headers: {
          'conversation_id': convId,
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });

      if (response.ok) {
        const data = await response.json();

        const loadedConversation = {
          name: data.title,
          conversationId: data.id,
          messages: data.messages.map(msg => ({
            text: msg.content,
            fromMe: msg.message_type === 'user',
            timestamp: msg.created_at,
            status: 'read',
            id: msg.id,
            sceneBreakdown: msg.message_type === 'ai' ? {
              genre: msg.genre,
              characters: msg.characters,
              locations: msg.locations,
              num_scenes: msg.num_scenes,
              shooting_days: msg.shooting_days,
              vfx_required: msg.vfx_required,
              complexity_score: msg.complexity_score
            } : null
          }))
        };

        setConversations([loadedConversation]);
      }
    } catch (error) {
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognitionInstance = new SpeechRecognition();
        recognitionInstance.continuous = true;
        recognitionInstance.interimResults = true;
        recognitionInstance.lang = 'en-US';

        recognitionInstance.onresult = (event) => {
          const transcript = Array.from(event.results)
            .map(result => result[0])
            .map(result => result.transcript)
            .join('');
          setInputValue(prev => prev + ' ' + transcript);
        };

        recognitionInstance.onerror = (event) => {
          setRecognitionError('Error occurred in recognition: ' + event.error);
          setIsRecording(false);
        };

        setRecognition(recognitionInstance);
      } else {
        setRecognitionError('Speech recognition not supported in this browser');
      }
    }
  }, []);

  async function checkProjectPermissions(page) {
    try {
      // getUserById instead of getUserIDByToken - same single call, but its
      // response also includes user_type at no extra round trip, so the
      // free-tier check below can be skipped entirely for non-students
      // instead of always paying for a /free-tier/status call to learn that.
      const userIdRes = await fetch(`${baseUrl}/getUserById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("access_token")}`,
        },
      });

      if (!userIdRes.ok) {
        navigate("/login");
        return;
      }

      const { user_id, user_type } = await userIdRes.json();
      setActiveUser(user_id);

      const projectMembersRes = await fetch(`${baseUrl}/getMembersByProjectId`, {
        headers: { project_id: projectId }
      });

      if (!projectMembersRes.ok) {
        window.alert("Failed to check project permissions");
        navigate("/");
        return;
      }

      const projectMembersData = await projectMembersRes.json();
      const projectMemberList = projectMembersData.map(m => m.user_id);

      if (!projectMemberList.includes(user_id)) {
        window.alert("This user does not have access to this project!");
        navigate("/");
        return;
      }

      // Free-tier bypass: a currently-eligible verified student skips the
      // subscription check below entirely - checked independently via
      // GET /free-tier/status (same pattern StudentToolSelector.jsx already
      // uses) rather than trusted from navigation state. Only called for
      // user_type === 'student' - a non-student never pays for this extra
      // round trip at all. Anyone else (non-student, or a student who isn't
      // currently eligible - expired, etc.) falls through to the exact
      // existing check below, unchanged.
      if (user_type === 'student') {
        const freeTierRes = await fetch(`${baseUrl}/free-tier/status`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
        });
        if (freeTierRes.ok) {
          const freeTierStatus = await freeTierRes.json();
          if (freeTierStatus.state === 'active' || freeTierStatus.state === 'not_started') {
            return;
          }
        }
      }

      const toolsRes = await fetch(`${baseUrl}/getAvailableTools`, {
        headers: {
          project_id: projectId,
          Authorization: `Bearer ${localStorage.getItem("access_token")}`
        }
      });

      if (!toolsRes.ok) {
        window.alert("Failed to check tool permissions");
        navigate("/services");
        return;
      }

      const availableTools = await toolsRes.json();
      const toolNames = availableTools.map(tool => tool.name);

      if (!toolNames.includes(page)) {
        window.alert("You haven't purchased this tool yet!");
        navigate("/services");
      }
    } catch (err) {
      window.alert("Error checking permissions");
      navigate("/");
    }
  }

  // ✅ NEW: Fetch the real project record (genre, project_type) —
  // mirrors chatbot.jsx's fetchProjectInfo pattern exactly.
  async function fetchProjectInfo() {
    if (!projectId) return;
    try {
      const response = await fetch(`${baseUrl}/getProjectById`, {
        headers: {
          'project_id': projectId,
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setProjectInfo({
          genre: data.genre || '',
          project_type: data.project_type || ''
        });
      }
    } catch (error) {
    }
  }

  const handleInputChange = (event) => {
    setInputValue(event.target.value);
  };

  const handleSendClick = async () => {
    if (!inputValue.trim() && !uploadedFile) {
      showNotification('Please enter a message or upload a file', true);
      return;
    }

    setIsProcessing(true);

    try {
      if (uploadedFile) {
        await handleFileUploadToBackend(uploadedFile.file);
        return;
      }

      const response = await fetch(`${baseUrl}/scriptConversation/generateScript`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        },
        body: JSON.stringify({
          project_id: parseInt(projectId),
          prompt: inputValue,
          genre: projectInfo.genre || '',
          project_type: projectInfo.project_type || 'short_film',
          conversation_id: null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate script');
      }

      const result = await response.json();

      const newConversation = {
        name: 'Script Generation',
        conversationId: result.conversation_id,
        messages: [
          {
            text: inputValue,
            fromMe: true,
            timestamp: new Date().toISOString(),
            status: 'sent',
          },
          {
            text: result.script_text,
            fromMe: false,
            timestamp: new Date().toISOString(),
            status: 'read',
            id: result.message_id,
            sceneBreakdown: {
              genre: result.genre,
              characters: result.characters,
              locations: result.locations,
              num_scenes: result.num_scenes,
              shooting_days: result.shooting_days,
              vfx_required: result.vfx_required,
              complexity_score: result.complexity_score
            }
          }
        ]
      };

      setConversations([newConversation]);
      setActivePage(2);

      setInputValue('');
      setUploadedFile(null);

    } catch (error) {
      showNotification(error.message || 'Failed to generate script. Please try again.', true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoiceInput = () => {
    if (!recognition) {
      showNotification(recognitionError || 'Speech recognition not supported', true);
      return;
    }

    if (isRecording) {
      recognition.stop();
    } else {
      recognition.start();
      setInputValue('');
    }
    setIsRecording(!isRecording);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    const allowedTypes = [
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/jpeg',
      'image/png'
    ];
    const maxSize = 10 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      showNotification('Please upload a text, PDF, Word document, or image file', true);
      return;
    }

    if (file.size > maxSize) {
      showNotification('File size must be less than 10MB. Kindly try our Cloud Solutions for larger data.', true);
      return;
    }

    const filePreview = {
      name: file.name,
      type: file.type,
      size: file.size,
      url: URL.createObjectURL(file),
      file: file
    };

    setUploadedFile(filePreview);
    showNotification(`Successfully uploaded ${file.name}`);
  };

  const handleFileUploadToBackend = async (file) => {
    try {
      setUploadStatus('uploading');
      showNotification('Uploading file to cloud storage...');

      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'document');
      formData.append('user_id', activeUser);

      const uploadResponse = await fetch(`${baseUrl}/upload/direct`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        },
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('File upload failed');
      }

      const uploadResult = await uploadResponse.json();
      showNotification('File uploaded! Analyzing script...');

      const analysisFormData = new FormData();
      analysisFormData.append('project_id', projectId);
      analysisFormData.append('b2_file_url', uploadResult.public_url);
      analysisFormData.append('original_filename', uploadResult.original_filename);
      analysisFormData.append('genre', projectInfo.genre || '');
      analysisFormData.append('project_type', projectInfo.project_type || 'short_film');
      analysisFormData.append('generate_new', 'true');

      const analysisResponse = await fetch(`${baseUrl}/scriptConversation/uploadScriptForAnalysis`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem("access_token")}`
        },
        body: analysisFormData
      });

      if (!analysisResponse.ok) {
        const errorData = await analysisResponse.json();
        throw new Error(errorData.detail || 'Script analysis failed');
      }

      const result = await analysisResponse.json();

      const newConversation = {
        name: 'Script Analysis',
        conversationId: result.conversation_id,
        messages: [
          {
            text: `Uploaded: ${uploadResult.original_filename}`,
            fromMe: true,
            timestamp: new Date().toISOString(),
            status: 'sent',
            file: {
              name: uploadResult.original_filename,
              url: uploadResult.public_url
            }
          },
          {
            text: result.script_text || 'Script analysis complete!',
            fromMe: false,
            timestamp: new Date().toISOString(),
            status: 'read',
            id: result.message_id,
            sceneBreakdown: {
              genre: result.genre,
              characters: result.characters,
              locations: result.locations,
              num_scenes: result.num_scenes,
              shooting_days: result.shooting_days,
              vfx_required: result.vfx_required,
              complexity_score: result.complexity_score
            }
          }
        ]
      };

      setConversations([newConversation]);
      setActivePage(2);
      setUploadStatus('');
      setUploadedFile(null);
      showNotification('Script analysis complete!');

    } catch (error) {
      showNotification(error.message || 'Upload failed. Please try again.', true);
      setUploadStatus('');
    }
  };

  const removeUploadedFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderFilePreview = () => {
    if (!uploadedFile) return null;

    const isImage = ['image/jpeg', 'image/png'].includes(uploadedFile.type);

    return (
      <div className="flex items-center bg-teal-50 p-3 rounded-lg mt-4">
        {isImage ? (
          <img
            src={uploadedFile.url}
            alt="File preview"
            className="w-16 h-16 object-cover rounded mr-4"
          />
        ) : (
          <div className="w-16 h-16 bg-teal-100 rounded mr-4 flex items-center justify-center">
            <FaFileUpload className="text-teal-600 text-2xl" />
          </div>
        )}
        <div className="flex-grow">
          <p className="text-sm font-medium text-teal-800">{uploadedFile.name}</p>
          <p className="text-xs text-teal-600">
            {(uploadedFile.size / 1024).toFixed(2)} KB
          </p>
        </div>
        <button
          onClick={removeUploadedFile}
          className="text-red-500 hover:text-red-700"
        >
          <FaTimes />
        </button>
      </div>
    );
  };

  const handleDeviceUpload = () => {
    fileInputRef.current?.click();
  };

  const handleGoogleDriveUpload = () => {
    showNotification('Google Drive integration coming soon!');
  };

  const handleCloudUpload = () => {
    showNotification('Cloud upload integration coming soon!');
  };

  const showNotification = (message, isError = false) => {
    setAlertMessage(message);
    setAlertType(isError ? 'error' : 'success');
    setShowAlert(true);
    setTimeout(() => setShowAlert(false), 3000);
  };

  const renderPage = () => {
    // ✅ Show loading while checking for existing conversations
    if (isCheckingExisting) {
      return (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading your script session...</div>
          <div className="loading-subtext">Please wait while we check for existing scripts</div>
        </div>
      );
    }

    switch (activePage) {
      case 1:
        return (
          <>
            <div className="cinema-background">
              {[...Array(5)].map((_, index) => (
                <motion.div
                  key={index}
                  className="film-icon"
                  style={{
                    position: 'absolute',
                    left: `${index * 20}%`,
                    top: `${index * 15 + 10}%`,
                  }}
                  animate={{
                    y: [0, 10, 0],
                    rotate: [0, 180, 360],
                    opacity: [0.1, 0.15, 0.1],
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    delay: index * 2,
                  }}
                >
                  <Film size={80} color="rgba(213, 217, 6, 0.8)" />
                </motion.div>
              ))}

              {[...Array(4)].map((_, index) => (
                <motion.div
                  key={index}
                  className="camera-icon"
                  style={{
                    position: 'absolute',
                    left: `${index * 25 + 15}%`,
                    top: `${index * 20 + 10}%`,
                  }}
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 360],
                    opacity: [0.1, 0.2, 0.1],
                  }}
                  transition={{
                    duration: 12,
                    repeat: Infinity,
                    delay: index * 3,
                  }}
                >
                  <Camera size={60} color="rgba(14, 255, 191, 0.8)" />
                </motion.div>
              ))}
            </div>

            <div className="container mx-auto px-4 py-16 relative z-20">
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="text-center mb-12"
              >
                <h1 className="text-6xl font-extrabold text-teal-800 mb-6 hover:animate-pulse">
                  Script AI Generator
                </h1>
                <p className="text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
                  Transform your creative process with AI-powered scriptwriting that revolutionizes storytelling, saves time, and unleashes your creative potential.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="max-w-2xl mx-auto bg-gradient-to-r from-white to-teal-50 rounded-2xl shadow-2xl p-8 border border-teal-200 hover:bg-gradient-to-r hover:from-teal-50 hover:to-teal-100 transition-all"
              >
                <div className="relative mb-6">
                  <input
                    type="text"
                    className="w-full px-4 py-3 rounded-xl border-2 border-teal-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-200 transition-all hover:bg-teal-50"
                    placeholder="Enter your thoughts and script details or requirements..."
                    value={inputValue}
                    onChange={handleInputChange}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !isProcessing) {
                        handleSendClick();
                      }
                    }}
                    disabled={isProcessing}
                  />
                  <div className={styles.scriptPromptInputButtons}>
                    <Tippy content={isRecording ? "Stop Recording" : "Start Voice Input"}>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        className={`p-2 rounded-lg transition-colors ${isRecording
                          ? 'bg-red-400/30 animate-pulse'
                          : 'bg-teal-400/20 hover:bg-teal-400/30'
                        }`}
                        onClick={handleVoiceInput}
                        disabled={isProcessing}
                      >
                        <GrMicrophone className={`text-xl ${isRecording ? 'text-red-400' : 'text-teal-400'}`} />
                      </motion.button>
                    </Tippy>

                    <Tippy content={isProcessing ? "Processing..." : "Send Prompt"}>
                      <motion.button
                        whileHover={{ scale: isProcessing ? 1 : 1.1 }}
                        className={`p-2 rounded-lg transition-colors ${
                          isProcessing
                            ? 'bg-gray-400/30 cursor-not-allowed'
                            : 'bg-amber-400/20 hover:bg-amber-400/30'
                        }`}
                        onClick={handleSendClick}
                        disabled={isProcessing}
                      >
                        {isProcessing ? (
                          <div className="animate-spin w-5 h-5 border-2 border-amber-400 border-t-transparent rounded-full" />
                        ) : (
                          <FiSend className="text-xl text-amber-400" />
                        )}
                      </motion.button>
                    </Tippy>
                  </div>
                </div>

                {showAlert && (
                  <div
                    className={`mb-6 p-4 rounded-lg text-center ${
                      alertType === 'error'
                        ? 'bg-red-100 text-red-700 border border-red-300'
                        : 'bg-green-100 text-green-700 border border-green-300'
                    }`}
                  >
                    {alertMessage}
                  </div>
                )}

                <div className="text-center">
                  <h5 className="text-xl text-teal-600 mb-4">Upload Existing Script</h5>
                  <input
                    type="file"
                    ref={fileInputRef}
                    className={`${styles.uploadScriptInp} hover:bg-teal-50`}
                    accept=".txt,.pdf,.doc,.docx,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileUpload(e.target.files?.[0])}
                    disabled={isProcessing}
                  />
                  {renderFilePreview()}
                </div>

                <div className={styles.uploadSelector}>
                  <Tippy content="Upload from Device">
                    <button
                      className="px-6 py-3 bg-teal-50 text-teal-600 rounded-lg border border-teal-500 hover:bg-teal-100 transition-all duration-300 flex items-center space-x-2"
                      onClick={handleDeviceUpload}
                      disabled={uploadStatus === 'uploading' || isProcessing}
                    >
                      <PiFolderSimpleLight className="text-xl" />
                      <span>Your Device</span>
                    </button>
                  </Tippy>

                  <Tippy content="Google Drive Upload">
                    <button
                      className="px-6 py-3 bg-teal-50 text-teal-600 rounded-lg border border-teal-500 hover:bg-teal-100 transition-all duration-300 flex items-center space-x-2"
                      onClick={handleGoogleDriveUpload}
                    >
                      <FaGoogleDrive className="text-xl" />
                      <span>Google Drive</span>
                    </button>
                  </Tippy>

                  <Tippy content="Cloud Upload">
                    <button
                      className="px-6 py-3 bg-teal-50 text-teal-600 rounded-lg border border-teal-500 hover:bg-teal-100 transition-all duration-300 flex items-center space-x-2"
                      onClick={handleCloudUpload}
                    >
                      <FaCloudDownloadAlt className="text-xl" />
                      <span>Cloud</span>
                    </button>
                  </Tippy>
                </div>

                <div className="grid md:grid-cols-3 gap-8 mb-16">
                  {benefitsData.map((benefit, index) => (
                    <Tippy
                      key={index}
                      content={benefit.details}
                      placement="top"
                      theme="light"
                    >
                      <motion.div
                        className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all text-center"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <div className="flex justify-center mb-4">
                          {benefit.icon}
                        </div>
                        <h3 className="text-xl font-semibold text-teal-600 mb-2">
                          {benefit.title}
                        </h3>
                        <p className="text-gray-500">{benefit.description}</p>
                      </motion.div>
                    </Tippy>
                  ))}
                </div>
              </motion.div>
            </div>
          </>
        );

      case 2:
        return (
          <ChatBot
            initialConversations={conversations}
            initialSelectedConversationIndex={selectedConversationIndex}
            projectId={projectId}
          />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <SEO
        title="Script Writing Tool"
        description="Write, analyse, and develop your film script with ProducerBuddy's AI-powered script writing and breakdown tool."
        url="https://producerbuddy.pilomation.com/script"
        robots="noindex, nofollow"
      />
      <style>{animationStyles}</style>
      <SubNav />
      <div id={styles.mainContainer}>
        {renderPage()}
      </div>
    </>
  );
};

export default ScriptAi;
